import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { FiltroConteudo } from '@core/models/conteudo.models';
import { environment } from '@env/environment';
import { MatPageable } from '@core/table/mat-table';
import { EStorage } from '@shared/utils/enums/storage.enum';
import { StorageService } from '@shared/utils/services/storage.service';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';


const routes = {
  cadastrar: `conteudo/cadastrar`,
  buscarPorValores: (pagina: number, registros: number) => `conteudo/buscaporvalores/${pagina}/${registros}`,
  buscarPorId: (id: number) => `conteudo/buscaporid/${id}`,
  alterar: `conteudo/alterar`,
  inativar: `conteudo/alterarstatus`,
  reativar: `conteudo/alterarstatus`,
  excluir: `conteudo/excluir`,
  buscarCategorias: `conteudo/categorias`,
  uploadImagem: `conteudos/imagens`,
  buscarSlidersHome: `conteudo/home/sliders`,
  buscarNovidadesHome: `conteudo/home/novidades`,
  buscarNoticiasHome: `conteudo/home/noticias`,
  buscarImagens: `conteudos/imagens`,
  buscarNovidadesPorId: (id) => `conteudo/novidades/buscaporid/${id}`,
  buscarTodasNovidades: (pagina: number, registros: number) => `conteudo/novidades/buscatodos/${pagina}/${registros}`

}

@Injectable({
  providedIn: 'root'
})
export class ConteudoService {

  constructor(private apiService: ApiService,
    private _storageService: StorageService
  ) { }


  buscarPorValores(filtro: FiltroConteudo) {
    let params = new HttpParams();

    if (filtro.titulo) {
      params = params.set('titulo', filtro.titulo);
    }
    if (filtro.idTipoConteudo !== undefined && filtro.idTipoConteudo !== null) {
      params = params.set('idTipoConteudo', filtro.idTipoConteudo.toString());
    }
    if (filtro.idStatusConteudo !== undefined && filtro.idStatusConteudo !== null) {
      params = params.set('idStatusConteudo', filtro.idStatusConteudo.toString());
    }
    if (filtro.dataInicio) {
      params = params.set('dataInicio', filtro.dataInicio);
    }
    if (filtro.colunaOrdenar) {
      params = params.set('colunaOrdenar', filtro.colunaOrdenar);
    }
    if (filtro.tipoOrdenacao) {
      params = params.set('tipoOrdenacao', filtro.tipoOrdenacao);
    }

    return this.apiService.get(routes.buscarPorValores(filtro.pagina, filtro.registros), params);
  }

  buscarPorId(id) {
    return this.apiService.get(routes.buscarPorId(id));
  }

  reativar(ids: string[]) {
    return this.apiService.put<any>(routes.reativar, {
      conteudo: {
        idStatusConteudo: 1,
        idsConteudo: ids
      }
    });
  }

  inativar(ids: string[]) {
    return this.apiService.put<any>(routes.inativar, {
      conteudo: {
        idStatusConteudo: 2,
        idsConteudo: ids
      }
    });
  }

  excluir(id: string) {
    return this.apiService.put<any>(routes.excluir, {
      conteudo: {
        id: Number(id)
      }
    });
  }

  buscarCategorias() {
    return this.apiService.get<any>(routes.buscarCategorias);
  }

  uploadImagemCapa(imagem: { nomeArquivo: string, conteudoArquivo: string }) {
    return this.apiService.postCustomApi<any>(`${environment.lote}/${routes.uploadImagem}`, imagem);
  }

  cadastrar(payload: any) {
    return this.apiService.post<any>(routes.cadastrar, {
      conteudo: {
        ... payload
      }
    });
  }

  alterar(payload: any) {
    return this.apiService.put<any>(routes.alterar,
      {
        conteudo: {
          ... payload
        }
      })
  }

  buscarSlidersHome() {
    return this.apiService.get<any>(routes.buscarSlidersHome);
  }

  buscarNovidadesHome() {
    return this.apiService.get<any>(routes.buscarNovidadesHome);
  }

  buscarNoticiasHome() {
    return this.apiService.get<any>(routes.buscarNoticiasHome);
  }

  buscarImagens(chaveImagem: any) {
    let params = new HttpParams();
    params = params.set('chaveImagem', chaveImagem)
    return this.apiService.downloadGetCustomApi(`${environment.lote}/${routes.buscarImagens}`, params)
  }

  buscarConteudosComImagens(conteudos: any[]): Observable<any[]> {
    const imagensStorage = this._storageService.get({ storageKey: EStorage.IMAGES })?.value || {};
    const requests = conteudos.map(conteudo =>
      imagensStorage[conteudo.chaveImagem]
        ? of({ ...conteudo, urlImagem: imagensStorage[conteudo.chaveImagem] })
        : this.buscarImagens(conteudo.chaveImagem).pipe(
            switchMap(response =>
              response.status !== 200 || !response.body
                ? of({ ...conteudo, urlImagem: '' })
                : from(this.blobToBase64(response.body)).pipe(
                    map(base64 => ({
                      ...conteudo,
                      urlImagem: base64
                    }))
                  )
            ),
            catchError(() => of({ ...conteudo, urlImagem: '' }))
          )
    );

    return forkJoin(requests).pipe(
      map(slidesComImagem => {
        this.salvarImagensNoStorage(slidesComImagem);
        return slidesComImagem;
      })
    );
  }

  buscarNovidadesPorId(id) {
    return this.apiService.get<any>(routes.buscarNovidadesPorId(id))
  }

  buscarTodasNovidades(pagination: MatPageable) {
    return this.apiService.get<any>(routes.buscarTodasNovidades(pagination.pagina, pagination.registros)
    )
  }

  blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  salvarImagensNoStorage(imagem): void {
    const imagensStorage = this._storageService.get({ storageKey: EStorage.IMAGES })?.value || {};
    const imagens = imagem.reduce((acc, slide) => ({
      ...acc,
      [slide.chaveImagem]: slide.urlImagem
    }), imagensStorage);

    this._storageService.set({
      storageKey: EStorage.IMAGES,
      data: imagens
    });
  }
}
